import { useState, useRef } from "react";
import { X, Mic, Square, Play, Pause, Loader2 } from "lucide-react";
import { supabase } from "../lib/supabaseClient.ts";
import type { User } from "../types.ts";

interface VoiceReportModalProps {
  open: boolean;
  onClose: () => void;
  user: User;
  onReportCreated: () => void;
}

export default function VoiceReportModal({ open, onClose, user, onReportCreated }: VoiceReportModalProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    village: "",
    crop: "maize",
    reportType: "pest" as "pest" | "disease" | "yield" | "weather" | "technique",
    severity: "medium" as "low" | "medium" | "high" | "critical"
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
    } catch (error) {
      console.error('Recording failed:', error);
      alert('Microphone access denied or not available');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const transcribeAudio = async () => {
    if (!audioBlob) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${backendUrl}/api/speech-to-text`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.text) {
        setTranscription(data.text);
      }
    } catch (error) {
      console.error('Transcription failed:', error);
      setTranscription("Transcription failed. Please type your report manually.");
    } finally {
      setLoading(false);
    }
  };

  const uploadAudio = async (blob: Blob): Promise<string | null> => {
    try {
      const fileName = `voice-report-${Date.now()}.webm`;
      const filePath = `voice-reports/${fileName}`;

      // Add timeout to upload operation
      const uploadPromise = supabase.storage
        .from('community-audio')
        .upload(filePath, blob);

      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Upload timeout')), 10000)
      );

      const { error: uploadError } = await Promise.race([uploadPromise, timeoutPromise]) as any;

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('community-audio')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Audio upload failed:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transcription.trim()) {
      alert('Please record and transcribe audio first');
      return;
    }

    setLoading(true);
    try {
      let audioUrl = null;
      if (audioBlob) {
        try {
          audioUrl = await uploadAudio(audioBlob);
        } catch (error) {
          console.warn('Audio upload failed, proceeding without audio:', error);
          // Continue without audio URL - report will still be created
        }
      }

      const { error } = await supabase
        .from('community_reports')
        .insert({
          farmer_id: user.id,
          farmer_name: user.user_metadata?.full_name || user.name || 'Anonymous Farmer',
          village: formData.village,
          crop: formData.reportType === 'weather' ? 'general' : formData.crop,
          report_type: formData.reportType,
          severity: formData.severity,
          description: transcription,
          audio_url: audioUrl,
          confidence: 70 // Voice reports get moderate confidence
        });

      if (error) {
        console.error('Database insertion error:', error);
        throw error;
      }

      onReportCreated();
      onClose();
      
      // Reset form
      setFormData({
        village: "",
        crop: "maize",
        reportType: "pest",
        severity: "medium"
      });
      setAudioBlob(null);
      setAudioUrl("");
      setTranscription("");
    } catch (error) {
      console.error('Report creation failed:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      alert(`Failed to create report: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Voice Report</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Village/Location</label>
            <input
              type="text"
              required
              value={formData.village}
              onChange={(e) => setFormData({...formData, village: e.target.value})}
              placeholder="e.g., Ha Abia, Maseru"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Crop</label>
              <select
                value={formData.reportType === 'weather' ? 'N/A' : formData.crop}
                onChange={(e) => setFormData({...formData, crop: e.target.value})}
                disabled={formData.reportType === 'weather'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:text-gray-500"
              >
                {formData.reportType === 'weather' ? (
                  <option value="N/A">N/A (Weather Report)</option>
                ) : (
                  <>
                    <option value="maize">Maize</option>
                    <option value="beans">Beans</option>
                    <option value="sorghum">Sorghum</option>
                    <option value="wheat">Wheat</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.reportType}
                onChange={(e) => setFormData({...formData, reportType: e.target.value as any})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="pest">Pest</option>
                <option value="disease">Disease</option>
                <option value="yield">Yield</option>
                <option value="weather">Weather</option>
                <option value="technique">Technique</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Voice Recording</label>
            <div className="flex items-center justify-center space-x-3 p-4 border-2 border-dashed border-gray-300 rounded-lg">
              {!isRecording && !audioBlob && (
                <button
                  type="button"
                  onClick={startRecording}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Mic className="w-4 h-4" />
                  <span>Start Recording</span>
                </button>
              )}
              
              {isRecording && (
                <button
                  type="button"
                  onClick={stopRecording}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  <Square className="w-4 h-4" />
                  <span>Stop Recording</span>
                </button>
              )}

              {audioBlob && (
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={playAudio}
                    className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    <span>{isPlaying ? 'Pause' : 'Play'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={transcribeAudio}
                    disabled={loading}
                    className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Transcribe"}
                  </button>
                </div>
              )}
            </div>
            
            {audioUrl && (
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
            )}
          </div>

          {transcription && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transcription</label>
              <textarea
                value={transcription}
                onChange={(e) => setTranscription(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Edit transcription if needed..."
              />
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !transcription.trim()}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}